
let baseUrl = ''
let defaultHeaders = {}

function sanitizeUrl(url: string): string {
  if (url.endsWith('/')) { return url.slice(0, -1) } else { return url }
}

export const setAiFeatureBaseUrl = (newUrl: string): void => { baseUrl = sanitizeUrl(newUrl) }

export const setAiFeatureDefaultHeaders = (headers: Object): void => { defaultHeaders = headers }

export interface GetV1AiFeatureSummaryResponse {
  report_type: string;
  session_id: string;
  summary: string;
  flags: Array<{
    severity: string;
    message: string;
    metric: string;
  }>;
  recommendations: string[];
  charts: Array<{
    chart_type: string;
    title: string;
    labels: string[];
    datasets: Array<{
      label: string;
      values: number[];
    }>;
    currency: string;
    unit: string | null;
    center_text: string | null;
  }>;
  usage: {
    service: string;
    session_id: string;
    model: string;
    input_tokens: number;
    output_tokens: number;
    cost: number;
    currency: string;
  };
}

interface V1AiFeatureSummaryRequest {
  data?: any;
  report_type?: string;
  companyId?: string;
  'x-token'?: string;
  'x-timezone'?: string;
}

export interface PostV1AiFeatureChatResponse {
  reply: string;
  [key: string]: any;
}

interface V1AiFeatureChatRequest {
  session_id: string;
  query: string;
  'x-token'?: string;
  'x-timezone'?: string | number;
}

export const streamV1AiFeatureChat = async (
  request: V1AiFeatureChatRequest,
  onToken: (token: string) => void,
  onDone: () => void,
  onError?: (message: string) => void,
  onOffTopic?: () => void
): Promise<void> => {
  const headers: Record<string, string> = {
    ...defaultHeaders,
    'Content-type': 'application/json; charset=utf-8'
  }
  if (request['x-token'] !== undefined) {
    headers['x-token'] = request['x-token']
  }
  if (request['x-timezone'] !== undefined) {
    headers['x-timezone'] = String(request['x-timezone'])
  }

  const { session_id, query } = request;

  const response = await fetch(`${baseUrl}/v1/summary/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ session_id, query })
  })

  if (!response.ok) {
    throw new Error(await response.text())
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      try {
        const msg = JSON.parse(line.slice(6))
        if (msg.type === 'token') onToken(msg.data)
        else if (msg.type === 'done') onDone()
        else if (msg.type === 'error') onError?.(msg.message)
        else if (msg.type === 'off_topic') onOffTopic?.()
      } catch {}
    }
  }
}

export interface V1CopilotChatRequest {
  query: string;
  doc_id?: string;
  doc_ids?: string[];
  doc_intro?: boolean;
  'x-token'?: string;
  'x-timezone'?: string | number;
}

export const streamV1CopilotChat = async (
  request: V1CopilotChatRequest,
  onToken: (token: string) => void,
  onDone: () => void,
  onError?: (message?: string) => void,
  onOffTopic?: () => void,
  onDocIntro?: (data: any) => void
): Promise<void> => {
  const headers: Record<string, string> = {
    ...defaultHeaders,
    'Content-Type': 'application/json; charset=utf-8'
  };
  if (request['x-token'] !== undefined) {
    headers['x-token'] = request['x-token'];
  }
  if (request['x-timezone'] !== undefined) {
    headers['x-timezone'] = String(request['x-timezone']);
  }

  const response = await fetch(`${baseUrl}/v1/copilot/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query: request.query, doc_id: request.doc_id, doc_ids: request.doc_ids, doc_intro: request.doc_intro })
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      let msg;
      try {
        msg = JSON.parse(line.slice(6));
      } catch {
        continue;
      }
      if (msg.type === 'token') onToken(msg.data);
      else if (msg.type === 'done') onDone();
      else if (msg.type === 'error') {
        onError?.(msg.message);
        throw new Error(msg.message || 'AI error');
      }
      else if (msg.type === 'off_topic') onOffTopic?.();
      else if (msg.type === 'doc_intro') onDocIntro?.(msg.data);
    }
  }
};

export const resetV1CopilotSession = async (token?: string): Promise<void> => {
  await fetch(`${baseUrl}/v1/copilot/reset`, {
    method: 'POST',
    headers: { ...defaultHeaders, ...(token ? { 'x-token': token } : {}) }
  });
};



export const postV1CopilotSubmit = async (sessionId: string, token: string, formType: string, fieldUpdates: any): Promise<any> => {
  const res = await fetch(`${baseUrl}/v1/copilot/submit`, {
    method: 'POST',
    headers: { ...defaultHeaders, 'Content-Type': 'application/json; charset=utf-8', 'x-token': token },
    body: JSON.stringify({ session_id: sessionId, form_type: formType, field_updates: fieldUpdates })
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export const postV1CopilotSaveDraft = async (sessionId: string, token: string, formType: string, fieldUpdates: any): Promise<any> => {
  const res = await fetch(`${baseUrl}/v1/copilot/save-draft`, {
    method: 'POST',
    headers: { ...defaultHeaders, 'Content-Type': 'application/json; charset=utf-8', 'x-token': token },
    body: JSON.stringify({ session_id: sessionId, form_type: formType, field_updates: fieldUpdates })
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export const postV1CopilotDiscard = async (sessionId: string, token: string, formType: string, fieldUpdates: any): Promise<any> => {
  const res = await fetch(`${baseUrl}/v1/copilot/discard`, {
    method: 'POST',
    headers: { ...defaultHeaders, 'Content-Type': 'application/json; charset=utf-8', 'x-token': token },
    body: JSON.stringify({ session_id: sessionId, form_type: formType, field_updates: fieldUpdates })
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export interface V1CopilotAgentRequest {
  form_type: string;
  'x-token'?: string;
  'x-timezone'?: string | number;
}

export const startV1CopilotAgent = async (
  request: V1CopilotAgentRequest,
  onToken: (token: string) => void,
  onDone: () => void,
  onError?: (message?: string) => void,
  onOffTopic?: () => void
): Promise<void> => {
  const headers: Record<string, string> = {
    ...defaultHeaders,
    'Content-Type': 'application/json; charset=utf-8'
  };
  if (request['x-token'] !== undefined) {
    headers['x-token'] = request['x-token'];
  }
  if (request['x-timezone'] !== undefined) {
    headers['x-timezone'] = String(request['x-timezone']);
  }

  const response = await fetch(`${baseUrl}/v1/copilot/agent/start`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ form_type: request.form_type })
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      let msg;
      try {
        msg = JSON.parse(line.slice(6));
      } catch {
        continue;
      }
      if (msg.type === 'token') onToken(msg.data);
      else if (msg.type === 'done') onDone();
      else if (msg.type === 'error') {
        onError?.(msg.message);
        throw new Error(msg.message || 'AI error');
      }
      else if (msg.type === 'off_topic') onOffTopic?.();
    }
  }
};

export const getV1CopilotQuestions = async (modules: string[], token: string): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (Array.isArray(modules)) {
    modules.forEach(m => queryParams.append('module', m));
  }
  
  const response = await fetch(`${baseUrl}/v1/copilot/questions?${queryParams.toString()}`, {
    method: 'GET',
    headers: { ...defaultHeaders, 'x-token': token }
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const data = await response.json();
  return data.data || data;
};

export interface V1CopilotFormAssistRequest {
  message: string;
  'x-token'?: string;
  'x-timezone'?: string | number;
}

export const formAssistV1Copilot = async (
  request: V1CopilotFormAssistRequest,
  onToken: (token: string) => void,
  onDone: () => void,
  onError?: (message?: string) => void,
  onFieldUpdates?: (data: Record<string, unknown>) => void,
  onAction?: (action: string) => void,
  onStatus?: (status: string) => void
): Promise<void> => {
  const headers: Record<string, string> = {
    ...defaultHeaders,
    'Content-Type': 'application/json; charset=utf-8'
  };
  if (request['x-token'] !== undefined) {
    headers['x-token'] = request['x-token'];
  }
  if (request['x-timezone'] !== undefined) {
    headers['x-timezone'] = String(request['x-timezone']);
  }

  const response = await fetch(`${baseUrl}/v1/copilot/agent/form-assist`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message: request.message })
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      let msg;
      try {
        msg = JSON.parse(line.slice(6));
      } catch {
        continue;
      }
      if (msg.type === 'token') onToken(msg.data);
      else if (msg.type === 'done') onDone();
      else if (msg.type === 'error') {
        onError?.(msg.message);
        throw new Error(msg.message || 'AI error');
      }
      else if (msg.type === 'field_updates') onFieldUpdates?.(msg.data);
      else if (msg.type === 'action') onAction?.(msg.data);
      else if (msg.type === 'status') onStatus?.(msg.data);
    }
  }
};

export interface SearchResult {
  id: string;
  source_id: number;
  doc_type: string;
  module: string;
  title: string;
  reference: string | null;
  status: string | null;
  date: string | null;
  amount: number | null;
  score: number;
  party_name: string | null;
  approval_status: string | null;
  transaction_type: string | null;
  email: string | null;
  phone: string | null;
  highlight: { title?: string[]; text_content?: string[] };
}

export interface GetV1AiFeatureSearchQueryResponse {
  success: boolean;
  status_code: number;
  data: { results: SearchResult[]; total: number; duration_ms: number };
  pagination: Record<string, any>;
}

interface V1AiFeatureSearchQueryRequest {
  query: string;
  'x-token'?: string;
  'x-timezone'?: string;
}

export const postV1AiFeatureSearchQuery = async (
  request: V1AiFeatureSearchQueryRequest
): Promise<GetV1AiFeatureSearchQueryResponse> => {
  const headers: Record<string, string> = {
    ...defaultHeaders,
    'Content-type': 'application/json; charset=utf-8',
  };
  if (request['x-token'] !== undefined) headers['x-token'] = request['x-token'];
  if (request['x-timezone'] !== undefined) headers['x-timezone'] = request['x-timezone'];

  const response = await fetch(`${baseUrl}/v1/search/query`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query: request.query }),
  });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

export const postV1AiFeatureSummary = async (request: V1AiFeatureSummaryRequest = {}): Promise<GetV1AiFeatureSummaryResponse> => {
  const headers: Record<string, string> = {
    ...defaultHeaders,
    'Content-type': 'application/json; charset=utf-8'
  }
  if (request['x-token'] !== undefined) {
    headers['x-token'] = request['x-token']
  }
  if (request['x-timezone'] !== undefined) {
    headers['x-timezone'] = request['x-timezone']
  }

  const { data, report_type, companyId } = request;

  const response = await fetch(`${baseUrl}/v1/summary`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      data: data || {},
      report_type: report_type || "aged_payable",
      companyId: companyId
    })
  })

  if (!response.ok) {
    throw new Error(await response.text())
  }

  return response.json()
}

// ── Document Intelligence endpoints ───────────────────────────────────────────

export interface UploadDocumentResponse {
  doc_id: string;
  session_id: string | null;
  status: string;
  filename: string;
  pages: number;
  deduplicated: boolean;
}

export interface DocumentStatusResponse {
  doc_id: string;
  filename: string | null;
  status: 'pending' | 'processing' | 'parsing' | 'embedding' | 'indexing' | 'ready' | 'failed';
  chunk_count: number | null;
  pages: number | null;
  category: string | null;
  sub_category: string | null;
  error: string | null;
  error_code: string | null;
}

export interface DeleteDocumentResponse {
  doc_id: string;
  status: 'deleted';
}

export const uploadDocument = async (
  file: File,
  token: string,
  sessionId?: string,
  onProgress?: (pct: number) => void
): Promise<UploadDocumentResponse> => {
  const formData = new FormData();
  formData.append('files', file);
  if (sessionId) formData.append('session_id', sessionId);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}/v1/document/upload`);
    xhr.setRequestHeader('x-token', token);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress?.(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const raw = JSON.parse(xhr.responseText);
        const data = raw.data !== undefined ? raw.data : raw;
        resolve(data.items ? data.items[0] : data);
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.detail || err.message || 'Upload failed'));
        } catch {
          reject(new Error(xhr.responseText || 'Upload failed'));
        }
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.send(formData);
  });
};

export const uploadDocuments = async (
  files: File[],
  token: string,
  sessionId?: string,
  onProgress?: (pct: number) => void
): Promise<{ items: UploadDocumentResponse[] }> => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  if (sessionId) formData.append('session_id', sessionId);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}/v1/document/upload`);
    xhr.setRequestHeader('x-token', token);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress?.(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const raw = JSON.parse(xhr.responseText);
        // raw or raw.data could be { items: [...] }
        const resData = raw.data !== undefined ? raw.data : raw;
        resolve(resData);
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.detail || err.message || 'Upload failed'));
        } catch {
          reject(new Error(xhr.responseText || 'Upload failed'));
        }
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.send(formData);
  });
};

export const getDocumentStatus = async (
  docId: string,
  token: string
): Promise<DocumentStatusResponse> => {
  const response = await fetch(`${baseUrl}/v1/document/${docId}/status`, {
    method: 'GET',
    headers: { 'x-token': token }
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to get document status');
  }
  const raw = await response.json();
  return raw.data !== undefined ? raw.data : raw;
};

export const deleteDocument = async (
  docId: string,
  token: string
): Promise<DeleteDocumentResponse> => {
  const response = await fetch(`${baseUrl}/v1/document/${docId}`, {
    method: 'DELETE',
    headers: { 'x-token': token }
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to delete document');
  }
  const raw = await response.json();
  return raw.data !== undefined ? raw.data : raw;
};

export interface VendorInsightOut {
  vendor_id: number;
  vendor_name: string | null;
  facts: {
    po_created: number;
    po_delivered: number;
    timely_delivery: number;
    missed_delivery: number;
    delivery_date_unknown: number;
    short_shipment_count: number;
    lead_time_kept: number;
    lead_time_missed: number;
    lead_time_unknown: number;
    price_pattern: Array<{
      item_id: number;
      item_name: string;
      first_price: number;
      last_price: number;
      pct_change: number;
      trend: string;
      n: number;
    }>;
    payment_terms_pattern: Record<string, number>;
    rfq_invited: number;
    rfq_responded: number;
    quick_response_items: string[];
    late_response_items: string[];
    no_response_items: string[];
  };
  insight: string;
}

export interface VendorInsightsResponse {
  company_id: number;
  vendors: VendorInsightOut[];
  top_recommendations: Array<{
    vendor_id: number;
    vendor_name: string | null;
    rank: number;
    reason: string;
  }>;
}

export interface V1AiVendorInsightsRequest {
  companyId: string | number;
  vendorIds: number[];
  'x-token'?: string;
  'x-timezone'?: string;
}

export const postV1AiVendorInsights = async (request: V1AiVendorInsightsRequest): Promise<VendorInsightsResponse> => {
  const headers: Record<string, string> = {
    ...defaultHeaders,
    'Content-type': 'application/json; charset=utf-8'
  }
  if (request['x-token'] !== undefined) {
    headers['x-token'] = request['x-token']
  }
  if (request['x-timezone'] !== undefined) {
    headers['x-timezone'] = request['x-timezone']
  }

  const { companyId, vendorIds } = request;

  const response = await fetch(`${baseUrl}/v1/vendor-insights`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      company_id: companyId,
      vendor_ids: vendorIds
    })
  })

  if (!response.ok) {
    throw new Error(await response.text())
  }

  return response.json()
}

export const postV1AiVendorProfile = async (request: V1AiVendorInsightsRequest): Promise<VendorInsightsResponse> => {
  const headers: Record<string, string> = {
    ...defaultHeaders,
    'Content-type': 'application/json; charset=utf-8'
  }
  if (request['x-token'] !== undefined) {
    headers['x-token'] = request['x-token']
  }
  if (request['x-timezone'] !== undefined) {
    headers['x-timezone'] = request['x-timezone']
  }

  const { companyId, vendorIds } = request;

  const response = await fetch(`${baseUrl}/v1/vendor-profile`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      company_id: companyId,
      vendor_ids: vendorIds
    })
  })

  if (!response.ok) {
    throw new Error(await response.text())
  }

  return response.json()
}
